import React from "react";

/**
 * Renders a user's department affiliations as a set of chips.
 *
 * - Home department: neutral text chip (matches existing "dept name" style).
 * - Additional managed departments: small indigo "+DeptName" pills — same
 *   convention as the Managers page and Departments cards so the pattern
 *   is consistent across the app.
 *
 * A user with no home dept and no managed depts renders as "—".
 *
 * Props:
 *   user           — user object with department_id + managed_department_ids
 *   depts          — array of { id, name } from DataContext
 *   size           — "sm" (default) or "xs" for dense tables
 *   showHomeChip   — when true, wrap home dept in a chip too. Default false
 *                    keeps the home dept as plain text so existing tables
 *                    don't visually change for single-dept users.
 */
export const DeptChips = ({ user, depts = [], size = "sm", showHomeChip = false, "data-testid": testId }) => {
    const byId = React.useMemo(() => Object.fromEntries((depts || []).map((d) => [d.id, d])), [depts]);
    if (!user) return <span className="text-slate-400 italic">—</span>;
    const homeName = user.department_id ? (byId[user.department_id]?.name || "—") : null;
    const managed = Array.isArray(user.managed_department_ids) ? user.managed_department_ids : [];
    const extras = managed
        .filter((id) => id && id !== user.department_id && byId[id])
        .map((id) => ({ id, name: byId[id].name }));

    if (!homeName && extras.length === 0) {
        return <span className="text-slate-400 italic">—</span>;
    }

    const pillClasses =
        size === "xs"
            ? "text-[10px] px-1.5 py-0.5"
            : "text-[11px] px-2 py-0.5";

    return (
        <span className="inline-flex flex-wrap items-center gap-1.5" data-testid={testId}>
            {homeName && (
                showHomeChip ? (
                    <span
                        title="Home department"
                        className={`inline-flex items-center rounded-md font-medium bg-slate-100 text-slate-700 border border-slate-200 ${pillClasses}`}
                    >
                        {homeName}
                    </span>
                ) : (
                    <span className="text-slate-700">{homeName}</span>
                )
            )}
            {extras.map((d) => (
                <span
                    key={d.id}
                    data-testid={`dept-chip-additional-${user.user_id || ""}-${d.id}`}
                    title={`Also manages ${d.name}`}
                    className={`inline-flex items-center gap-0.5 rounded-full font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200 ${pillClasses}`}
                >
                    <span className="text-[9px] uppercase tracking-wider text-indigo-500 font-bold">+</span>
                    {d.name}
                </span>
            ))}
        </span>
    );
};

export default DeptChips;
